'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Shield,
  List,
  Users,
} from 'lucide-react';

export default function RolesLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useTranslation();

  // Roles management sub-feature navigation
  const tabs = [
    {
      id: 'system-roles',
      name: t.organization.roles.systemRoles || 'System Roles',
      path: '/organization/roles/system-roles',
      icon: Shield,
      color: '#3370ff',
      bgColor: '#e1eaff',
    },
    {
      id: 'workflow-roles',
      name: t.organization.roles.workflowRoles || 'Workflow Roles',
      path: '/organization/roles/workflow-roles',
      icon: Users,
      color: '#00b42a',
      bgColor: '#dff4e8',
    },
    {
      id: 'permissions',
      name: t.organization.roles.permissionList || 'Permissions',
      path: '/organization/roles/permissions',
      icon: List,
      color: '#722ed1',
      bgColor: '#f2e8ff',
    },
  ];

  const isActive = (path: string) => {
    // Exact match or match with sub-paths
    return pathname === path || pathname.startsWith(path + '/');
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* Feishu style: Horizontal Tab navigation, unified height h-12 (48px) */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center gap-8 h-full">
            {/* Tab buttons */}
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = isActive(tab.path);
              
              return (
                <button
                  key={tab.id}
                  onClick={() => router.push(tab.path)}
                  className="relative flex items-center gap-2 px-4 h-full text-sm font-medium transition-colors"
                  style={{
                    color: active ? '#1f2329' : '#646a73'
                  }}
                >
                  <div 
                    className="w-5 h-5 rounded flex items-center justify-center"
                    style={{ backgroundColor: active ? tab.bgColor : 'transparent' }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: active ? tab.color : '#8f959e' }} />
                  </div>
                  {tab.name}
                  {active && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: '#3370ff' }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
}

